import { useEffect, useState } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { MediaDto } from '@b-stock/listing-api-client'

import { getLegacyBpStaticPath } from '@/buyer-portal/buyUrlPrefix'
import { FeatureFlags } from '@/helpers/featureFlags'
import { extractVideosFromDescription } from '@/helpers/video/videoLinkParser'
import { getThumbnailUrls } from '@/helpers/video/videoThumbnails'

import type { ImageType, MediaType, VideoType } from './types'
import { useAsyncThumbnails } from './useAsyncThumbnails'

const placeholder = getLegacyBpStaticPath('images/placeholder-image.png')

const placeholderImage: ImageType = {
  urls: {
    large: placeholder,
    medium: placeholder,
    original: placeholder,
    thumb: placeholder,
  },
  isPlaceholder: true,
}

const computeMergedMedia = (
  media: MediaDto[] | undefined,
  description: string | undefined,
  enableVideoInGallery: boolean
): MediaType[] => {
  const lotImages: ImageType[] = media?.length ? media : []

  const videoItems: VideoType[] =
    enableVideoInGallery && description
      ? extractVideosFromDescription(description).map((video) => ({
          type: 'video',
          urls: getThumbnailUrls(video),
          videoData: video,
        }))
      : []

  const merged = [...lotImages, ...videoItems]
  return merged.length > 0 ? merged : [placeholderImage]
}

export const useMergedMedia = (
  media: MediaDto[] | undefined,
  description: string | undefined
): MediaType[] => {
  const enableVideoInGallery = useFlag(
    FeatureFlags.ENABLE_VIDEO_IN_MEDIA_GALLERY
  )

  const [allMedia, setAllMedia] = useState<MediaType[]>(() =>
    computeMergedMedia(media, description, enableVideoInGallery)
  )

  useEffect(() => {
    setAllMedia(computeMergedMedia(media, description, enableVideoInGallery)) // oxlint-disable-line react/set-state-in-effect
  }, [media, enableVideoInGallery, description])

  useAsyncThumbnails(allMedia, enableVideoInGallery, setAllMedia)

  return allMedia
}
